import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, ExternalLink, Check, X, Eye, CheckCheck, RefreshCw } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { listProviderUpdateRequests, updateProviderRequestStatus, removeProviderAndNotify } from "@/lib/seo-quality.functions";
import { toast } from "sonner";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/dashboard/admin/requests")({
  head: () => ({ meta: [{ title: "Update Requests, Heallexa Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: RequestsPage,
});

type Row = Awaited<ReturnType<typeof listProviderUpdateRequests>>["rows"][number];
type Cursor = { created_at: string; id: string } | null;
const STATUSES = ["new", "under_review", "approved", "rejected", "completed", "all"] as const;

const STATUS_STYLE: Record<string, string> = {
  new: "bg-primary/10 text-primary border-primary/20",
  under_review: "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-400",
  approved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-400",
  rejected: "bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-400",
  completed: "bg-muted text-muted-foreground border-border",
};

function StatusPill({ s }: { s: string }) {
  const cls = STATUS_STYLE[s] ?? "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center h-5 px-1.5 rounded text-[10px] uppercase tracking-wider font-medium border ${cls}`}>
      {s.replace("_", " ")}
    </span>
  );
}

function RequestsPage() {
  const { user, loading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const list = useServerFn(listProviderUpdateRequests);
  const update = useServerFn(updateProviderRequestStatus);
  const removeFn = useServerFn(removeProviderAndNotify);

  const [status, setStatus] = useState<(typeof STATUSES)[number]>("new");
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<Cursor[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const reload = (s: typeof status, cursor: Cursor = null) => {
    setBusy(true);
    list({ data: { status: s, limit: 50, cursor } })
      .then((res) => { setRows(res?.rows ?? []); setHasMore(res?.hasMore ?? false); })
      .catch((e: any) => {
        setRows([]); setHasMore(false);
        toast.error(e?.message?.includes("429") || e?.status === 429
          ? "Server is busy right now. Please retry in a moment."
          : (e?.message ?? "Failed to load requests"));
      })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (loading) return;
    if (!user || primaryRole !== "admin") { navigate({ to: "/login" }); return; }
    setCursorStack([null]); setPageIndex(0);
    reload(status, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, primaryRole, status]);

  const goNext = () => {
    if (!hasMore || rows.length === 0) return;
    const last = rows[rows.length - 1];
    const nextCursor: Cursor = { created_at: last.created_at, id: last.id };
    setCursorStack((s) => { const c = s.slice(0, pageIndex + 1); c.push(nextCursor); return c; });
    setPageIndex((p) => p + 1);
    reload(status, nextCursor);
  };
  const goPrev = () => {
    if (pageIndex === 0) return;
    const prev = pageIndex - 1;
    setPageIndex(prev);
    reload(status, cursorStack[prev] ?? null);
  };

  const setStatusFor = async (id: string, newStatus: "under_review" | "approved" | "rejected" | "completed") => {
    try {
      await update({ data: { id, status: newStatus } });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      reload(status);
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  const handleRemove = async (r: Row) => {
    setRemoving(r.id);
    try {
      const res = await removeFn({ data: { providerId: r.provider_id, requestId: r.id } });
      toast.success(res.emailed ? "Listing removed & provider notified" : "Listing removed (no email on file)");
      reload(status);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to remove");
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return <div className="pt-32 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const counts = STATUSES.reduce<Record<string, number>>((acc, s) => {
    acc[s] = s === "all" ? rows.length : rows.filter(r => r.status === s).length;
    return acc;
  }, {});

  const tabs = (
    <div className="flex items-center gap-0 -mb-px">
      {STATUSES.map(s => {
        const active = status === s;
        return (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`h-9 px-3 text-[13px] capitalize border-b-2 transition-colors ${
              active
                ? "border-foreground text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.replace("_", " ")}
            {status === s && rows.length > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">{counts[s]}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <AdminPageShell
      title="Update Requests"
      description="Claims, corrections, and removal requests from providers."
      primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: () => reload(status) }}
    >
      <div className="border-b border-border mb-4">{tabs}</div>
      <div className="card-surface rounded-md overflow-hidden">
        {busy ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-muted-foreground">No requests in this bucket.</p>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-medium px-4 py-2 w-[110px]">Type</th>
                <th className="text-left font-medium px-4 py-2 w-[140px]">Provider</th>
                <th className="text-left font-medium px-4 py-2">From</th>
                <th className="text-left font-medium px-4 py-2">Message</th>
                <th className="text-left font-medium px-4 py-2 w-[110px]">Status</th>
                <th className="text-right font-medium px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 capitalize text-foreground">{r.request_type.replace("_", " ")}</td>
                  <td className="px-4 py-2.5">
                    <a
                      href={`/providers/${r.provider_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline font-mono text-[12px]"
                    >
                      {r.provider_id.slice(0, 8)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-foreground truncate max-w-[180px]">{r.submitted_by_name ?? "—"}</div>
                    <div className="text-muted-foreground text-xs truncate max-w-[180px]">{r.submitted_by_email ?? ""}</div>
                  </td>
                  <td className="px-4 py-2.5 max-w-sm text-muted-foreground truncate" title={r.message ?? ""}>
                    {r.message ?? "—"}
                  </td>
                  <td className="px-4 py-2.5"><StatusPill s={r.status} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-0.5">
                      <IconBtn label="Review" onClick={() => setStatusFor(r.id, "under_review")}><Eye className="h-3.5 w-3.5" /></IconBtn>
                      <IconBtn label="Approve" onClick={() => setStatusFor(r.id, "approved")}><Check className="h-3.5 w-3.5" /></IconBtn>
                      <IconBtn label="Reject" onClick={() => setStatusFor(r.id, "rejected")}><X className="h-3.5 w-3.5" /></IconBtn>
                      <IconBtn label="Mark done" onClick={() => setStatusFor(r.id, "completed")}><CheckCheck className="h-3.5 w-3.5" /></IconBtn>
                      <div className="w-px h-4 bg-border mx-1" />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            title="Remove & notify"
                            disabled={removing === r.id}
                            className="h-7 w-7 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-50"
                          >
                            {removing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove listing & notify provider?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This hides the listing from the directory and emails{" "}
                              <span className="font-medium text-foreground">{r.submitted_by_email ?? "the provider on file"}</span>{" "}
                              that it has been removed. Reversible by an admin.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemove(r)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove & notify
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
        <div>Showing {rows.length === 0 ? 0 : pageIndex * 50 + 1}–{pageIndex * 50 + rows.length}{hasMore ? " · more available" : ""}</div>
        <div className="flex items-center gap-1">
          <button disabled={pageIndex === 0 || busy} onClick={goPrev} className="h-7 px-2 border border-border disabled:opacity-50">Prev</button>
          <span className="px-2">Page {pageIndex + 1}</span>
          <button disabled={!hasMore || busy} onClick={goNext} className="h-7 px-2 border border-border disabled:opacity-50">Next</button>
        </div>
      </div>
    </AdminPageShell>
  );
}

function IconBtn({ children, onClick, label }: { children: React.ReactNode; onClick: () => void; label: string }) {
  return (
    <button
      title={label}
      onClick={onClick}
      className="h-7 w-7 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
    >
      {children}
    </button>
  );
}
